import { Checkbox, FileButton, Flex, Modal, Text } from "@mantine/core";
import { IconUpload } from "@tabler/icons-react";
// import { useParams } from "react-router-dom";
// import { useUploadFileApi } from "src/hooks";
import { type ChangeEvent, Fragment, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useIsExpired } from "src/generator/hooks/useIsExpired";
import { useCheckPerm } from "src/hooks";
import { type IFile } from "src/interfaces";

// import { Notification } from "src/utils/Notification";
import { Button } from "../../Button/Button";
import ScrollableTable from "../../ScrollableTable/ScrollableTable";
import FileRow from "./FileRow/FileRow";
import Gallery from "./Gallery/Gallery";
import { getHeader } from "./FilesModal.utils";

// import { useFilesModal } from "./FilesModal.hooks";

interface Props {
  opened?: boolean;
  close?: () => void;
  readonly?: boolean;
  handleUpload?: any;
  isNewEntry?: boolean;
  isUploading?: boolean;
  showRemoved?: boolean;
  toggleRemoved?: (event: ChangeEvent<HTMLInputElement>) => void;
  _files?: any;
  removedNumber?: number;
  accept?: string;
  renderOnlyContent?: boolean;
  isMultipleAttachmentsModal?: boolean;
  overrideHandleRemove?: (filename: string) => void;
  title?: React.ReactNode | string;

  //Custom props
  customFileRowComponent?: React.ComponentType<any>;
  customHandleRemove?: (attachmentId: string) => Promise<void>;
  modalTitle?: string;
  hideRemovedToggle?: boolean;
  patientId?: string | null;
  disableUpload?: boolean;
  gallery?: boolean;
}

const FilesModal = ({
  opened,
  close,
  readonly,
  isNewEntry = false,
  handleUpload,
  isUploading,
  showRemoved,
  toggleRemoved,
  _files,
  removedNumber,
  accept,
  renderOnlyContent,
  isMultipleAttachmentsModal,
  overrideHandleRemove,
  title,
  customFileRowComponent: CustomFileRow,
  customHandleRemove,
  modalTitle,
  hideRemovedToggle = false,
  patientId,
  disableUpload = false,
  gallery = false,
}: Props) => {
  const checkPerm = useCheckPerm();
  const isExpiredLogbook = useIsExpired();
  const { pathname } = useLocation();

  const isReview = useMemo(
    () => pathname.includes("summary/reviews/review/"),
    [pathname],
  );

  const headers = useMemo(() => getHeader(isNewEntry), [isNewEntry]);

  const renderRow = useCallback(
    ({ row }: { row: IFile }) => {
      if (CustomFileRow) {
        return (
          <CustomFileRow
            row={row}
            readonly={readonly}
            onRemove={customHandleRemove}
          />
        );
      }
      return (
        <FileRow
          row={row}
          readonly={readonly}
          isMultipleAttachmentsModal={isMultipleAttachmentsModal}
          overrideHandleRemove={overrideHandleRemove}
          customHandleRemove={customHandleRemove}
        />
      );
    },
    [
      readonly,
      CustomFileRow,
      customHandleRemove,
      isMultipleAttachmentsModal,
      overrideHandleRemove,
    ],
  );

  const renderContent = () => (
    <Fragment>
      <Flex
        align="center"
        justify="space-between"
        gap={20}
        mb={!isMultipleAttachmentsModal && !gallery ? "lg" : undefined}
        pr="sm">
        {title && title}
        <Flex justify="flex-end" align="center" gap={20} style={{ flex: 1 }}>
          {patientId === null && modalTitle && (
            <Text span fw="normal" size="md" c="dimmed">
              (Patient ID not available)
            </Text>
          )}
          {!isNewEntry && !isMultipleAttachmentsModal && !hideRemovedToggle && (
            <Checkbox
              labelPosition="left"
              label={`Show removed (${removedNumber})`}
              checked={showRemoved}
              onChange={toggleRemoved}
            />
          )}
          {!readonly &&
            !isReview &&
            checkPerm("uploadfiles") &&
            !isExpiredLogbook &&
            !disableUpload && (
              <FileButton
                multiple
                onChange={handleUpload}
                accept={accept}
                disabled={patientId === null}>
                {props => (
                  <Button
                    leftSection={<IconUpload size={"1.4rem"} />}
                    loading={isUploading}
                    disabled={patientId === null}
                    {...props}
                    tabIndex={-1}>
                    Add File
                  </Button>
                )}
              </FileButton>
            )}
        </Flex>
      </Flex>
      {gallery ? (
        <Gallery
          files={_files}
          readonly={readonly}
          customHandleRemove={customHandleRemove}
          onRemove={overrideHandleRemove}
        />
      ) : (
        <ScrollableTable
          data={_files}
          headers={headers}
          renderRow={renderRow}
          keyExtractor={file => file.aid}
          style={{ height: isMultipleAttachmentsModal ? "200px" : "50vh" }}
        />
      )}
    </Fragment>
  );
  // for use in multiple attachments (summary)
  if (renderOnlyContent) return renderContent();
  return (
    <Modal
      size="1200px"
      opened={Boolean(opened)}
      onClose={close || (() => { })}
      title={
        modalTitle ? (
          <Text size="lg" fw="bold">
            {modalTitle}
          </Text>
        ) : (
          <Text size="lg" fw="bold">
            Files
            {isNewEntry && (
              <Text span fw="normal" size="md" ml="sm">
                (files will be added after saving the entry)
              </Text>
            )}
          </Text>
        )
      }>
      {renderContent()}
    </Modal>
  );
};

export default FilesModal;
